'use client';

import { c as _c } from "react/compiler-runtime";
import { usePathname, useSearchParams } from 'next/navigation';
import { Fragment, useCallback, useMemo } from 'react';
import useSWR from 'swr';
import ViverseWorldCard from '@/components/ViverseWorldCard';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { GENRE_PAGE_LIMIT } from '@/constants/genre';
import { useConfigsContext } from '@/context/ConfigsContext';
import { cn } from '@/lib/utils';
import { getCardType } from '@/lib/viverse-world-card';
import { getPublicRoomsV3, RoomDetail } from '@/services/cms';
type GenreWorldsProps = {
  initialWorlds: RoomDetail[];
  worldsCount: number;
  genre: string;
};
export default function GenreWorlds(t0) {
  const $ = _c(32);
  const {
    initialWorlds,
    worldsCount,
    genre
  } = t0;
  const {
    store
  } = useConfigsContext();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const htcToken = store?.credentials?.htcToken;
  let t1;
  if ($[0] !== searchParams) {
    t1 = Number(searchParams.get("page")) || 1;
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const page = t1;
  const currentPage = page > 0 ? page : 1;
  const startValue = (currentPage - 1) * GENRE_PAGE_LIMIT;
  const totalPagesValue = Math.ceil(worldsCount / GENRE_PAGE_LIMIT) || 1;
  let t2;
  if ($[2] !== startValue || $[3] !== totalPagesValue) {
    t2 = {
      start: startValue,
      totalPages: totalPagesValue
    };
    $[2] = startValue;
    $[3] = totalPagesValue;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    start,
    totalPages
  } = t2;
  let t3;
  if ($[5] !== genre || $[6] !== htcToken || $[7] !== start) {
    t3 = () => getPublicRoomsV3({
      tag: genre,
      start,
      limit: GENRE_PAGE_LIMIT,
      exclude_persona: true,
      content_types: ["experiences", "games", "videos", "templates"]
    }, htcToken).then(_temp);
    $[5] = genre;
    $[6] = htcToken;
    $[7] = start;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== initialWorlds) {
    t4 = {
      fallbackData: initialWorlds
    };
    $[9] = initialWorlds;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    data: worlds
  } = useSWR(htcToken ? `genreDetail-${genre}${start}` : null, t3, t4);
  let t5;
  if ($[11] !== pathname || $[12] !== searchParams) {
    t5 = page_0 => {
      const params = new URLSearchParams(searchParams.toString());
      if (page_0 === 1) {
        params.delete("page");
      } else {
        params.set("page", String(page_0));
      }
      const query = params.toString();
      return query ? `${pathname}?${query}` : pathname;
    };
    $[11] = pathname;
    $[12] = searchParams;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const getPageUrl = t5;
  let t6;
  bb0: {
    if (totalPages <= 1) {
      let t7;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = [];
        $[14] = t7;
      } else {
        t7 = $[14];
      }
      t6 = t7;
      break bb0;
    }
    let t7;
    if ($[15] !== currentPage || $[16] !== totalPages) {
      const pages = new Set([1, currentPage, totalPages]);
      if (currentPage > 1) {
        pages.add(currentPage - 1);
      }
      if (currentPage < totalPages) {
        pages.add(currentPage + 1);
      }
      t7 = Array.from(pages).sort(_temp2);
      $[15] = currentPage;
      $[16] = totalPages;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    t6 = t7;
  }
  const paginationPages = t6;
  let t7;
  if ($[18] !== genre || $[19] !== store?.credentials?.htcToken || $[20] !== worlds) {
    t7 = worlds?.map(result => <ViverseWorldCard key={`${genre}-${result.id}`} info={result} className="h-auto p-0" imageClassName="aspect-video w-full object-cover" cardType={getCardType(result.content_type)} hideLiked={!store?.credentials?.htcToken} disableAutoPreview={true} hidePremiumBadge={false} />);
    $[18] = genre;
    $[19] = store?.credentials?.htcToken;
    $[20] = worlds;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t7) {
    t8 = <div className="1.5xl:grid-cols-5 3xl:grid-cols-7 grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-2 lg:grid-cols-4 lg:gap-2">{t7}</div>;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== currentPage || $[25] !== getPageUrl || $[26] !== paginationPages || $[27] !== totalPages) {
    t9 = totalPages > 1 && <Pagination><PaginationContent className="gap-5"><PaginationItem><PaginationPrevious className={cn("p-0", {
            "pointer-events-none": currentPage === 1
          })} iconClassName={cn("size-6", {
            "stroke-vive-primary-60": currentPage === 1
          })} hideText={true} href={getPageUrl(currentPage - 1)} /></PaginationItem>{paginationPages.map((pageNum, index) => {
          const prevPage = paginationPages[index - 1];
          const needsEllipsis = prevPage && pageNum - prevPage > 1;
          const isActive = pageNum === currentPage;
          return <Fragment key={pageNum}>{needsEllipsis && <PaginationItem><PaginationEllipsis /></PaginationItem>}<PaginationItem><PaginationLink className={cn("text-vive-primary-20 size-6 rounded-full border-0", {
                "bg-vive-primary-70 text-white": isActive
              })} href={getPageUrl(pageNum)} isActive={isActive}>{pageNum}</PaginationLink></PaginationItem></Fragment>;
        })}<PaginationItem><PaginationNext className={cn("p-0", {
            "pointer-events-none": currentPage >= totalPages
          })} iconClassName={cn("size-6", {
            "stroke-vive-primary-60": currentPage >= totalPages
          })} hideText={true} href={getPageUrl(currentPage + 1)} /></PaginationItem></PaginationContent></Pagination>;
    $[24] = currentPage;
    $[25] = getPageUrl;
    $[26] = paginationPages;
    $[27] = totalPages;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t8 || $[30] !== t9) {
    t10 = <section className="flex flex-col gap-6 pb-20">{t8}{t9}</section>;
    $[29] = t8;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}
function _temp2(a, b) {
  return a - b;
}
function _temp(res) {
  const worldData = res?.results?.results || [];
  return worldData;
}